import React from "react";
import moment, { Moment } from "moment";
import { withStyles } from "@material-ui/core";
import { ContactDetail } from "@components/ContactDetail";
import { Small } from "@components/typography/Small";
import locale from "@bonliva-crm/locale";

type Week = Date | string | Moment | { week: string; year: string };

export type WeekRangeProps = {
  from: Week;
  to: Week;
  vacancies?: number;
  /**
   * from 0 to 1
   */
  busyRatio?: number;
  withTitle?: boolean;
  showWeekPrefix?: boolean;
};

type Props = WeekRangeProps & {
  classes: any;
};

/**
 * Renders something like:
 * Vecka: 18 2017 - 25 2017
 * for contact cards
 */
export const WeekRange: React.FC<Props> = (props) => {
  const { classes, from, to, showWeekPrefix = true } = props;

  let fromWeek: string;
  let fromYear: string;
  let toWeek: string;
  let toYear: string;

  if (typeof from === "string" || from instanceof Date) {
    const m = moment(from);
    fromWeek = m.utc().format("W");
    fromYear = m.utc().format("GGGG");
  } else if (moment.isMoment(from)) {
    fromWeek = from.format("W");
    fromYear = from.format("GGGG");
  } else {
    fromWeek = from.week;
    fromYear = from.year;
  }

  if (typeof to === "string" || to instanceof Date) {
    const m = moment(to);
    toWeek = m.utc().format("W");
    toYear = m.utc().format("GGGG");
  } else if (moment.isMoment(to)) {
    toWeek = to.format("W");
    toYear = to.format("GGGG");
  } else {
    toWeek = to.week;
    toYear = to.year;
  }

  const vacancies = typeof props.vacancies === "number" ? `${props.vacancies} vk.` : null;
  const busyRatio =
    typeof props.busyRatio === "number" ? `${props.busyRatio * 100}%` : null;
  const postfix = [vacancies, busyRatio].filter((v) => v).join(", ");

  return (
    <ContactDetail
      className={classes.period}
      title={props.withTitle ? "Period" : undefined}
    >
      {showWeekPrefix && locale.Week + ": "}
      <strong>{fromWeek}</strong>
      <Small color="textSecondary" className={classes.year}>
        {fromYear}
      </Small>
      {(fromWeek !== toWeek || fromYear !== toYear) && (
        <>
          <span className={classes.dash}>&ndash;</span>
          <strong>{toWeek}</strong>
          <Small color="textSecondary" className={classes.year}>
            {toYear}
          </Small>
        </>
      )}
      {Boolean(postfix) && <Small className={classes.postfix}>({postfix})</Small>}
    </ContactDetail>
  );
};

WeekRange.defaultProps = {
  withTitle: true
};

const styles = (theme) => ({
  dash: {
    padding: `0 ${theme.spacing(1 / 2)}px`
  },

  period: {
    whiteSpace: "nowrap",
    "& span": {
      display: "inline-block"
    }
  },

  year: {
    paddingLeft: theme.spacing(1 / 2)
  },

  postfix: {
    display: "inline-block",
    marginLeft: theme.spacing()
  }
});

export default withStyles(styles as any)(WeekRange);

export function weekToDate(input: Week) {
  if (typeof input === "string" || input instanceof Date) {
    const m = moment.utc(input);
    return m.toDate();
  } else if (moment.isMoment(input)) {
    return input.toDate();
  } else {
    const m = moment.utc(`${input.week}-${input.year}`, "WW-GGGG");
    return m.toDate();
  }
}
